import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { PaginatedResponse } from 'types';
import { RootState } from 'store/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { IPlayerNotification } from 'types/notifications';
import { NotificationService } from 'api/notifications';

type NotificationsStateType = {
  data: IPlayerNotification[];
  count: number;
  unreadCount: number;
  isLoading: boolean;
};

type NotificationsActionsType = {
  get: () => void;
  getUnreadCount: () => void;
  updateNotificationById: (notificationId: number) => void;
  addNotification: (notification: IPlayerNotification) => void;
};

const initialState: NotificationsStateType = {
  isLoading: false,
  count: 0,
  unreadCount: 0,
  data: [],
};

const getNotifications = createAsyncThunk<PaginatedResponse<IPlayerNotification[]>>('notifications/get', () =>
  NotificationService.getNotificationsRequest()
);

const getUnreadNotificationsCount = createAsyncThunk<number>('notifications/getUnreadCount', () =>
  NotificationService.getUnreadNotificationsCountRequest()
);

const updateNotification = createAsyncThunk<IPlayerNotification, number>('notifications/updateById', (notificationId) =>
  NotificationService.updateNotificationById(notificationId)
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<IPlayerNotification>): void => {
      state.data = [...state.data, action.payload];
      state.unreadCount = state.unreadCount + 1;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getNotifications.pending.type, (state): void => {
        state.isLoading = true;
      })
      .addCase(
        getNotifications.fulfilled.type,
        (state, action: PayloadAction<PaginatedResponse<IPlayerNotification>>): void => {
          state.isLoading = false;
          state.data = action.payload.data;
          state.count = action.payload.count;
        }
      )
      .addCase(getNotifications.rejected.type, (state): void => {
        state.isLoading = false;
        state.data = [];
      })
      .addCase(updateNotification.pending.type, (state): void => {
        state.isLoading = true;
      })
      .addCase(updateNotification.fulfilled.type, (state, action: PayloadAction<IPlayerNotification>): void => {
        state.isLoading = false;
        state.data = state.data.map((notification) => {
          if (notification.id === action.payload.id) {
            return {
              ...notification,
              ...action.payload,
            };
          }
          return notification;
        });
        state.unreadCount = state.unreadCount - 1;
      })
      .addCase(updateNotification.rejected.type, (state): void => {
        state.isLoading = false;
      })
      .addCase(getUnreadNotificationsCount.fulfilled.type, (state, action: PayloadAction<number>): void => {
        state.unreadCount = action.payload;
      }),
});

const useNotifications = (): [NotificationsStateType, NotificationsActionsType] => {
  const state = useAppSelector((storeState: RootState) => storeState.notifications);
  const dispatch = useAppDispatch();

  const actions = {
    get: (): void => {
      dispatch(getNotifications());
    },
    getUnreadCount(): void {
      dispatch(getUnreadNotificationsCount());
    },
    updateNotificationById: (notificationId: number): void => {
      const notification = state.data.find(({ id }) => id === notificationId);
      const isAlreadyRead = !!notification?.seenAt;

      if (!isAlreadyRead) {
        dispatch(updateNotification(notificationId));
      }
    },
    addNotification: (notification: IPlayerNotification): void => {
      dispatch(notificationsSlice.actions.addNotification(notification));
    },
  };

  return [state, actions];
};

export { notificationsSlice };

export default useNotifications;
